/**
 * THE UNIFIED SEARCHABLE PAGE INDEX for the command palette.
 *
 * 🚨 ONE SOURCE, derived from the nav model (src/lib/nav.ts). Global search used to carry its OWN
 * hand-written ~43-page list that disagreed with nav.ts and covered barely half the site, so real
 * tools (rvol, gap, halts, squeeze...) returned nothing. That is the exact divergence nav.ts's header
 * documents. This index is BUILT from nav.ts, so search and navigation can never drift apart again.
 *
 * nav.ts covers the navigable pages; EXTRAS adds high-intent pages that live OUTSIDE the nav (the
 * screeners + data reports users actually type); KEYWORDS augments the search terms a page's label+desc
 * miss (e.g. "relative volume" must find RVOL Scanner). Admin pages are excluded (public search only).
 *
 * ⚠ NO EM DASHES in any user-facing string here (labels render in the palette). Same rule as nav.ts.
 */
import type { LucideIcon } from 'lucide-react'
// Lazy icons, like the nav (register mobile-first-paint-1021): see @/lib/navIcon.
import { I } from '@/lib/navIcon'
import { SIDEBAR_GROUPS } from '@/lib/nav'

export type SearchablePage = { href: string; label: string; Icon: LucideIcon; group: string; hay: string }

/** Extra search terms the label+desc miss for high-intent pages. These are what users actually type. */
const KEYWORDS: Record<string, string> = {
  '/rvol-scanner': 'rvol relative volume unusual volume spike momentum runners find',
  '/gap-scanner': 'gap up down gappers overnight breakout',
  '/stock-halts-today': 'halt halted luld circuit breaker resume trading',
  '/biggest-gainers-today': 'gainers up winners top best',
  '/biggest-losers-today': 'losers down decliners worst',
  '/most-active-stocks': 'active volume liquid dollar',
  '/premarket-movers': 'premarket pre market early before open',
  '/after-hours-movers': 'after hours post market extended earnings',
  '/low-float-movers': 'low float microcap squeeze runner rotation',
  '/betting-against': 'short interest shorted bearish squeeze puts',
  '/accumulation': 'accumulation obv money flow smart buying',
  '/ema-slope': 'ema slope trend 200 moving average',
  '/52-week-highs': '52 week high new highs breakout',
  '/money-map': 'sector flow market cap rotation added wiped',
  '/smart-money': 'institutional money flow 13f hedge funds whales institutional buying latest moves this week insider buys new stakes where institutions are buying',
  '/screener': 'screen filter scan custom criteria',
  '/why-are-stocks-moving-today': 'why moving catalyst reason news',
  '/stock-market-today': 'market today dashboard movers',
  '/top-stocks': 'top best ranked leaderboard strongest',
  '/dividends': 'dividend yield ex date income',
  '/earnings-calendar': 'earnings report eps',
  '/economic-calendar': 'economic fed cpi jobs macro',
  '/trending': 'trending hot popular buzz',
  '/insider-buying': 'insider form 4 executives buying',
}

/** Real pages that live OUTSIDE the nav model but that users search for. Confirmed-live routes only. */
const EXTRAS: { href: string; label: string; Icon: LucideIcon; group: string; kw: string }[] = [
  { href: '/short-squeeze-stocks', label: 'Short-Squeeze Stocks', Icon: I.Flame, group: 'Movers & Scanners', kw: 'short squeeze high short interest days to cover low float' },
  { href: '/most-shorted-stocks', label: 'Most Shorted Stocks', Icon: I.TrendingDown, group: 'Movers & Scanners', kw: 'most shorted short interest heavily bearish' },
  { href: '/most-volatile-stocks', label: 'Most Volatile Stocks', Icon: I.Activity, group: 'Movers & Scanners', kw: 'volatile volatility atr widest range swings' },
  { href: '/penny-stocks', label: 'Penny Stocks', Icon: I.Coins, group: 'Movers & Scanners', kw: 'penny cheap under 5 sub 5 dollar' },
  { href: '/overbought-stocks', label: 'Overbought Stocks', Icon: I.TrendingUp, group: 'Movers & Scanners', kw: 'overbought rsi stretched high extended' },
  { href: '/oversold-stocks', label: 'Oversold Stocks', Icon: I.TrendingDown, group: 'Movers & Scanners', kw: 'oversold rsi stretched low dip bounce' },
  { href: '/most-halted-stocks', label: 'Most Halted Stocks', Icon: I.Ban, group: 'Data reports', kw: 'most halted halt count leaders 30 days luld' },
  { href: '/track-record', label: 'Track Record', Icon: I.Trophy, group: 'Data reports', kw: 'track record performance signal proof measured alerts' },
  // Account/billing lives outside the nav but is high-intent search (a user reported they could not find
  // where to cancel, 2026-09-06). Keep the cancel words here so "cancel"/"unsubscribe"/"billing" resolve.
  { href: '/subscribe', label: 'Subscription & Billing', Icon: I.Coins, group: 'Account', kw: 'cancel subscription unsubscribe stop being charged billing manage plan upgrade downgrade refund membership payment premium' },
]

function buildIndex(): SearchablePage[] {
  const seen = new Set<string>()
  const out: SearchablePage[] = []
  for (const g of SIDEBAR_GROUPS) {
    if ('admin' in g && g.admin) continue // public search only
    const group = g.label || 'General'
    for (const it of g.items) {
      if (seen.has(it.href)) continue
      seen.add(it.href)
      out.push({ href: it.href, label: it.label, Icon: it.Icon, group,
        hay: `${it.label} ${it.desc} ${group} ${KEYWORDS[it.href] || ''}`.toLowerCase() })
    }
  }
  for (const e of EXTRAS) {
    if (seen.has(e.href)) continue
    seen.add(e.href)
    out.push({ href: e.href, label: e.label, Icon: e.Icon, group: e.group,
      hay: `${e.label} ${e.group} ${e.kw}`.toLowerCase() })
  }
  return out
}

export const PAGE_INDEX: SearchablePage[] = buildIndex()

/**
 * Fuzzy score a page against a query. Subsequence match on the LABEL with word-boundary + prefix
 * bonuses (so "gap scan" ranks Gap Scanner high), then a haystack fallback where every query token must
 * appear somewhere in label+desc+group+keywords (so "relative volume" finds RVOL Scanner via keywords).
 * Returns -Infinity for no match.
 */
export function scorePage(query: string, page: SearchablePage): number {
  const q = query.toLowerCase().replace(/\s+/g, ' ').trim()
  if (!q) return 0
  const L = page.label.toLowerCase()
  if (L === q) return 1000
  if (L.startsWith(q)) return 850
  const wi = L.indexOf(q)
  if (wi >= 0) { const boundary = wi === 0 || /[\s-]/.test(L[wi - 1]); return (boundary ? 560 : 380) - wi }
  let qi = 0, streak = 0, s = 0, first = -1
  for (let i = 0; i < L.length && qi < q.length; i++) {
    if (L[i] === q[qi]) { if (first < 0) first = i; const b = i === 0 || /[\s-]/.test(L[i - 1]); s += 12 + streak * 7 + (b ? 14 : 0); streak++; qi++ }
    else streak = 0
  }
  if (qi === q.length) return s - first * 0.4
  const toks = q.split(' ')
  if (toks.every((t) => page.hay.includes(t))) return 150 - (page.hay.indexOf(toks[0]) || 0) * 0.15
  return -Infinity
}

export function searchPages(query: string, limit = 7): SearchablePage[] {
  return PAGE_INDEX
    .map((p) => ({ p, s: scorePage(query, p) }))
    .filter((x) => x.s > -Infinity)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.p)
}
